// Manual operations panel — surfaces the Phase 5 trigger endpoints as
// dashboard buttons. Each click queues a force flag in kv; the bot loop
// picks it up on the next tick and fires the action.
//
// No optimistic UI here: we post, show a transient "queued" toast, and
// rely on the next /api/actions poll to confirm the action ran. That
// keeps the dashboard process out of the on-chain critical path.
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { CircleArrowUp, Coins, Repeat, Wallet } from 'lucide-react';
import { Button } from './ui/button';
import { SectionHeader } from './ui/section-header';
import { strategiesApi } from '../lib/api';

/** Subset of StrategyDetail the panel actually needs. Compatible with
 *  what /api/state returns under `state.strategy` so the Overview can
 *  pass it directly without an extra fetch. */
export interface StrategyPanelInput {
  id: number;
  slug: string;
  displayName: string;
  status: string;
  positionNftId: string | null;
  rangePct: number;
  /** DEX venue — hides the harvest button for gaugeless venues. */
  venue?: 'aerodrome' | 'uniswap_v3';
}
import { useCurrentUser } from '../hooks/useApi';
import { cn, formatCurrency } from '../lib/utils';

interface Props {
  strategy: StrategyPanelInput | null;
}

type Toast = { kind: 'success' | 'error'; text: string };

export function OperationsPanel({ strategy }: Props) {
  const qc = useQueryClient();
  const { data: me } = useCurrentUser();
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<Toast | null>(null);
  const [openModal, setOpenModal] = useState<
    null | 'mint' | 'deposit' | 'topup' | 'withdraw'
  >(null);

  const canWrite = !me || me.role === 'admin' || me.role === 'fund_manager';

  // Why a separate "disabled" reason: even authorized users shouldn't
  // be able to mint into a strategy that already has a position, etc.
  const hasPosition = strategy?.positionNftId != null;

  async function run(label: string, fn: () => Promise<unknown>) {
    if (!strategy) return;
    setBusy(label);
    try {
      await fn();
      setToast({ kind: 'success', text: `${label} queued` });
      // Bot acts on next tick; refresh actions after a brief delay
      setTimeout(() => qc.invalidateQueries({ queryKey: ['actions'] }), 1500);
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      setToast({ kind: 'error', text: `${label}: ${msg}` });
    } finally {
      setBusy(null);
      setTimeout(() => setToast(null), 4000);
    }
  }

  if (!strategy) {
    return null;
  }

  return (
    <div className="rounded-lg border border-border bg-surface p-6">
      <SectionHeader
        title="Manual operations"
        subtitle={
          canWrite
            ? 'Override automated cooldowns and thresholds. Bot acts on next tick.'
            : 'Read-only view — operations require admin or fund_manager role.'
        }
        size="sm"
        className="mb-4"
      />
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Button
          variant="outline"
          size="md"
          onClick={() => setOpenModal('mint')}
          disabled={!canWrite || hasPosition || busy !== null}
          title={hasPosition ? 'Strategy already has a position — use Redeploy instead' : undefined}
        >
          <Coins className="h-4 w-4" />
          Mint position…
        </Button>
        <Button
          variant="outline"
          size="md"
          onClick={() => run('Redeploy', () => strategiesApi.redeploy(strategy.id))}
          disabled={!canWrite || !hasPosition || busy !== null}
        >
          <Repeat className="h-4 w-4" />
          {busy === 'Redeploy' ? 'Queuing…' : 'Trigger redeploy'}
        </Button>
        {strategy.venue !== 'uniswap_v3' && (
          <Button
            variant="outline"
            size="md"
            onClick={() => run('Harvest', () => strategiesApi.harvest(strategy.id))}
            disabled={!canWrite || !hasPosition || busy !== null}
          >
            <Coins className="h-4 w-4" />
            {busy === 'Harvest' ? 'Queuing…' : 'Trigger harvest'}
          </Button>
        )}
        <Button
          variant="outline"
          size="md"
          onClick={() => run('Hedge', () => strategiesApi.hedge(strategy.id))}
          disabled={!canWrite || !hasPosition || busy !== null}
        >
          <CircleArrowUp className="h-4 w-4" />
          {busy === 'Hedge' ? 'Queuing…' : 'Trigger hedge'}
        </Button>
        <Button
          variant="outline"
          size="md"
          onClick={() => setOpenModal('deposit')}
          disabled={!canWrite || busy !== null}
        >
          <Wallet className="h-4 w-4" />
          + Deposit
        </Button>
        <Button
          variant="outline"
          size="md"
          onClick={() => setOpenModal('withdraw')}
          disabled={!canWrite || busy !== null}
        >
          <Wallet className="h-4 w-4" />
          − Withdraw
        </Button>
      </div>

      {toast && (
        <div
          className={cn(
            'mt-4 rounded-md border p-3 text-sm',
            toast.kind === 'success'
              ? 'border-positive/40 bg-positive/10 text-positive'
              : 'border-negative/40 bg-negative/10 text-negative',
          )}
        >
          {toast.text}
        </div>
      )}

      {openModal === 'mint' && (
        <MintModal
          strategy={strategy}
          onClose={() => setOpenModal(null)}
          onDone={(msg) => {
            setToast({ kind: 'success', text: msg });
            setTimeout(() => qc.invalidateQueries({ queryKey: ['actions'] }), 1500);
            setTimeout(() => qc.invalidateQueries({ queryKey: ['state'] }), 1500);
          }}
        />
      )}
      {(openModal === 'deposit' || openModal === 'topup' || openModal === 'withdraw') && (
        <CapitalModal
          strategy={strategy}
          kind={openModal}
          onClose={() => setOpenModal(null)}
          onDone={(msg) => {
            setToast({ kind: 'success', text: msg });
            setTimeout(() => qc.invalidateQueries({ queryKey: ['state'] }), 500);
          }}
        />
      )}
    </div>
  );
}

// ── Modals ───────────────────────────────────────────────────────────────

function Modal({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-lg border border-border bg-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 pt-6 pb-2">
          <h3 className="text-lg font-semibold text-foreground">{title}</h3>
        </div>
        <div className="px-6 pb-6 space-y-4">{children}</div>
      </div>
    </div>
  );
}

function MintModal({
  strategy,
  onClose,
  onDone,
}: {
  strategy: StrategyPanelInput;
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [ethBudget, setEthBudget] = useState('0.012');
  const [usdcBudget, setUsdcBudget] = useState('26');
  const [rangePct, setRangePct] = useState(String(strategy.rangePct));
  const [slippageBps, setSlippageBps] = useState('100');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    setSubmitting(true);
    setError(null);
    try {
      await strategiesApi.mint(strategy.id, {
        ethBudget: Number(ethBudget),
        usdcBudget: Number(usdcBudget),
        rangePct: Number(rangePct),
        slippageBps: Number(slippageBps),
      });
      onDone('Mint queued — bot will execute on next tick');
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title="Mint new position" onClose={onClose}>
      <Field label="ETH budget" suffix="ETH">
        <input
          type="number"
          step="0.001"
          min="0"
          value={ethBudget}
          onChange={(e) => setEthBudget(e.target.value)}
          className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </Field>
      <Field label="USDC budget" suffix="USDC">
        <input
          type="number"
          step="0.01"
          min="0"
          value={usdcBudget}
          onChange={(e) => setUsdcBudget(e.target.value)}
          className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </Field>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Range" suffix="%">
          <input
            type="number"
            step="0.5"
            min="0.5"
            max="50"
            value={rangePct}
            onChange={(e) => setRangePct(e.target.value)}
            className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </Field>
        <Field label="Slippage" suffix="bps">
          <input
            type="number"
            step="10"
            min="10"
            max="500"
            value={slippageBps}
            onChange={(e) => setSlippageBps(e.target.value)}
            className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </Field>
      </div>
      <p className="text-xs text-muted-foreground">
        Bot validates wallet balances ≥ budgets before submitting. If wallet
        is skewed (e.g. 95% WETH after a manual swap), run a balancing swap
        first via <code>npm run swap:balance</code> on the server.
      </p>
      {error && (
        <div className="rounded-md border border-negative/40 bg-negative/10 p-2 text-xs text-negative">
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="md" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button size="md" onClick={submit} disabled={submitting}>
          {submitting ? 'Submitting…' : 'Mint'}
        </Button>
      </div>
    </Modal>
  );
}

function CapitalModal({
  strategy,
  kind,
  onClose,
  onDone,
}: {
  strategy: StrategyPanelInput;
  kind: 'deposit' | 'topup' | 'withdraw';
  onClose: () => void;
  onDone: (msg: string) => void;
}) {
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const verb = kind === 'withdraw' ? 'Withdraw' : kind === 'topup' ? 'Top up' : 'Deposit';

  async function submit() {
    const n = Number(amount);
    if (!Number.isFinite(n) || n <= 0) {
      setError('Amount must be a positive number');
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const body = { amountUsd: n, note: note || undefined };
      const op =
        kind === 'deposit'
          ? strategiesApi.deposit
          : kind === 'topup'
          ? strategiesApi.topup
          : strategiesApi.withdraw;
      const result = await op(strategy.id, body);
      onDone(
        `${verb} recorded — deployed capital: ${formatCurrency(result.deployedCapitalUsd)}`,
      );
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal title={`${verb} capital`} onClose={onClose}>
      <p className="text-xs text-muted-foreground">
        Records the {kind} in the capital ledger and updates{' '}
        <code>deployed_capital_usd</code>. This is the growth-baseline figure
        — make sure it reflects what you actually moved into the strategy.
      </p>
      <Field label="Amount" suffix="USD">
        <input
          type="number"
          step="0.01"
          min="0"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          autoFocus
          className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </Field>
      <Field label="Note (optional)">
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. scale-up to $5k"
          className="w-full bg-transparent border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
      </Field>
      {error && (
        <div className="rounded-md border border-negative/40 bg-negative/10 p-2 text-xs text-negative">
          {error}
        </div>
      )}
      <div className="flex justify-end gap-2 pt-2">
        <Button variant="outline" size="md" onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          size="md"
          variant={kind === 'withdraw' ? 'destructive' : 'primary'}
          onClick={submit}
          disabled={submitting}
        >
          {submitting ? 'Submitting…' : verb}
        </Button>
      </div>
    </Modal>
  );
}

function Field({
  label,
  suffix,
  children,
}: {
  label: string;
  suffix?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-1">
        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
          {label}
        </span>
        {suffix && (
          <span className="text-xs text-muted-foreground tabular-nums">{suffix}</span>
        )}
      </div>
      {children}
    </label>
  );
}
